import { useCallback } from 'react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import invariant from 'tiny-invariant';

import { persistBreadcrumbs } from '@mopla/logger';
import { setWindowLocationHref } from '@mopla/utils';

import { usePaymentEnv } from '../context/paymentEnvContext';
import { usePaymentRestore } from '../context/paymentRestoreContext';
import { usePaymentStepStore } from '../stores/usePaymentStepStore';

import { buildTrueLayerAuthUrl } from './trueLayerUrlUtils';

/**
 * Hook implements navigation to TrueLayer Authorization and its completion.
 * Returns a handler connected to environment variables and store.
 *
 * Mechanism specifics:
 * WEB:
 * Complete loss of Mopla page context occurs, redirect to TrueLayer authorization side, and subsequent return to Mopla page.
 * ```
 * setWindowLocationHref(trueLayerAuthUrl);
 * ```
 *
 * Native:
 * TrueLayer opens in an in-app browser on top of the current Mopla page (i.e., without losing context, unmounting the React app).
 * ```
 * Browser.open({ url: trueLayerAuthUrl });
 * ```
 *
 * User completes TrueLayer authorization, after which a redirect to `redirect_uri` occurs, which was prepared earlier when opening TrueLayer
 * @see buildTrueLayerAuthUrl
 * */
export const useRedirectToTrueLayer = () => {
	const { trueLayerAuthLinkBase } = usePaymentEnv();
	const { restorePage, restoreQuery } = usePaymentRestore();
	const setPaymentStep = usePaymentStepStore((s) => s.setStep);

	return useCallback(() => {
		invariant(
			trueLayerAuthLinkBase,
			'[useRedirectToTrueLayer]: trueLayerAuthLinkBase is not set'
		);

		/**
		 * Setting the `sepaAuthentication` is required in the Native env TrueLayer flow.
		 * Otherwise, if the step is `sepa`, the PaymentPortal will be eventually closed, which is undesirable.
		 * */
		setPaymentStep('sepaAuthentication');

		const trueLayerAuthUrl = buildTrueLayerAuthUrl(
			trueLayerAuthLinkBase,
			restorePage,
			restoreQuery
		);

		if (Capacitor.isNativePlatform()) {
			Browser.open({ url: trueLayerAuthUrl });
		} else {
			/** Persist Sentry breadcrumbs before redirect to restore them after return */
			persistBreadcrumbs();
			setWindowLocationHref(trueLayerAuthUrl);
		}
	}, [restorePage, restoreQuery, setPaymentStep, trueLayerAuthLinkBase]);
};
